import PropTypes from 'prop-types';
import { POIPropType } from 'common/prop-types';
import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
  Fragment,
} from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import { Drawer, Icon, Select } from 'grab-ui';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import _trim from 'lodash/trim';
import _isEmpty from 'lodash/isEmpty';
import DeleteIcon from 'public/icons/delete-cross.svg';
import closeIcon from 'public/icons/close.svg';
import { suggestPOIsSelector } from 'selectors/geo-selectors';
import { getMobileOS } from 'helper/user';
import { injectUuidToPOI } from 'helper/geo-helper';

import {
  processPOIResponse,
  renderChildPOIOption,
  renderRootPOIOption,
} from './address-option';
import NumberedPin from '../../common/numbered-pin';
import styles from './styles.less';

export const AddressField = ({
  onChange,
  poi,
  isPickUp,
  showNumbers,
  number,
  placeholder,
  fetchAddressFunc,
  allowDelete,
  onDelete,
  autoFocus,
  suggestPOIs,
  presetKeyword,
  disabled,
  isRoundTrip,
  isPlaceholder,
  searchInOverlay,
  postSavingPoiCallback,
}) => {
  const { isIOS } = getMobileOS();
  const selectElemRef = useRef(null);

  // searchPOI states
  const [keyword, setKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResponse, setSearchResponse] = useState({});
  const [visibleOverlay, setVisibleOverlay] = useState(false);

  // current value
  const name = _get(poi, ['address', 'name']);
  const combinedAddress = _get(poi, ['address', 'combined_address']);
  const isSearchPOI = !_isEmpty(keyword);

  // searchPOI logic
  const onAddressSearch = useCallback(
    _debounce(keyword => {
      const trimmedKeyword = _trim(keyword);
      if (_isEmpty(trimmedKeyword)) {
        setKeyword('');
        setSearching(false);
        setSearchResponse({});
        return;
      }
      setKeyword(keyword);
      setSearching(true);
      fetchAddressFunc(keyword)
        .then(fetchResult => {
          // response param of /v3/go-poi is response, v1/v2 is resp
          const resp = fetchResult.response
            ? fetchResult.response
            : fetchResult.resp;
          setSearching(false);
          setSearchResponse(resp);
        })
        .catch(() => {
          setSearching(false);
          setSearchResponse({});
        });
    }, 300),
    [setKeyword, setSearchResponse, setSearching, fetchAddressFunc],
  );

  useEffect(
    () => {
      if (presetKeyword) {
        onAddressSearch(presetKeyword);
      }
    },
    [presetKeyword, onAddressSearch],
  );

  const { widthOverlay, placementOverlay } = useMemo(() => {
    if (window.innerWidth < 932) {
      return { widthOverlay: '100%', placementOverlay: 'bottom' };
    }
    return { widthOverlay: 495, placementOverlay: 'right' };
  }, []);

  const renderedOptions = useCallback(
    addressOptions => {
      const renderedAddressOptions = [];
      const { rootPOIs = [], childrenPOIs = {} } = addressOptions;
      rootPOIs.forEach(rootPOI => {
        const childrenPOI = childrenPOIs[rootPOI.id];
        const hasChildren = !_isEmpty(childrenPOI);
        renderedAddressOptions.push(
          renderRootPOIOption(
            rootPOI,
            hasChildren,
            postSavingPoiCallback,
            isPickUp,
          ),
        );
        if (hasChildren) {
          renderedAddressOptions.push(
            ...childrenPOI.map(childPOI =>
              renderChildPOIOption(childPOI, postSavingPoiCallback, isPickUp),
            ),
          );
        }
      });
      return renderedAddressOptions;
    },
    [isPickUp, postSavingPoiCallback],
  );

  // render searchPOI options
  const renderedAddressOptions = useMemo(
    () => {
      const { rootPOIs, childrenPOIs } = processPOIResponse(
        searchResponse,
        false,
      );
      return renderedOptions({ rootPOIs, childrenPOIs });
    },
    [searchResponse],
  );

  // render suggestPOI options
  const renderedSuggestOptions = useMemo(
    () => {
      const pois = _get(suggestPOIs, 'pois');
      const suggestFetching = _get(suggestPOIs, 'fetching');

      if (!suggestFetching && pois) {
        const { rootPOIs, childrenPOIs } = processPOIResponse(pois, true);
        return renderedOptions({ rootPOIs, childrenPOIs });
      }
      return undefined;
    },
    [suggestPOIs],
  );

  const onSelectPOI = useCallback(
    poiID => {
      let poi, poiMeta;
      const poiOptions = _get(selectElemRef, 'current.props.children', []).map(
        option => option.key,
      );
      if (isSearchPOI) {
        const uuid = _get(searchResponse, 'uuid');
        const pois = _get(searchResponse, 'result', []);
        poi = pois.find(poi => poi.id === poiID);
        injectUuidToPOI(poi, uuid);
        if (poiOptions && searchResponse) {
          poiMeta = {
            isRecommendedPoi: false,
            keyword,
            selectedIndex: poiOptions.indexOf(poiID),
            candidatePOIs: pois.map(poi => poi.id),
          };
        }
      } else {
        const pois = [
          ..._get(suggestPOIs, 'pois.result', []),
          ..._get(suggestPOIs, 'pois.extra', []),
        ];
        poi = pois.find(poi => poi.id === poiID);
        poiMeta = {
          isRecommendedPoi: true,
          selectedIndex: poiOptions.indexOf(poiID),
          candidatePOIs: pois.map(poi => poi.id),
          poiCategory: _get(poi, 'category'),
          poiLabel: _get(poi, 'label'),
        };
      }

      onChange(poi, poiMeta);

      if (searchInOverlay) {
        // delay so there's no many animation at same time (dropdown close & overlay closed)
        setTimeout(() => closeOverlay(), 300);
      }
    },
    [
      isSearchPOI,
      searchResponse,
      suggestPOIs,
      selectElemRef,
      keyword,
      onChange,
      searchInOverlay,
    ],
  );

  useEffect(
    () => {
      if (visibleOverlay || (autoFocus && poi === undefined)) {
        setTimeout(() => {
          const baseId = visibleOverlay
            ? 'overlayInputAddressField'
            : 'inputAddressField';
          const elem = document.getElementById(`${baseId}-${number}`);
          elem && elem.click();
          // more delay on overlay to make sure element is on correct position
        }, visibleOverlay ? 700 : 300);
      }
    },
    [visibleOverlay, autoFocus, poi],
  );

  const openOverlay = () => {
    setVisibleOverlay(true);
  };

  const closeOverlay = () => {
    setVisibleOverlay(false);
  };

  const fetching = isSearchPOI ? searching : _get(suggestPOIs, 'fetching');
  const options = isSearchPOI ? renderedAddressOptions : renderedSuggestOptions;

  const renderAddressSearchBar = (overlay = false) => {
    const primary = !overlay;
    const baseId = primary ? 'inputAddressField' : 'overlayInputAddressField';
    const primaryWithOverlay = primary && searchInOverlay; // primary component with search in overlay enabled

    return (
      <div className={styles.addressContainer}>
        <NumberedPin
          className={cx(styles.numberedPinOverwrite, {
            [styles.placeholder]: isPlaceholder,
          })}
          isSender={isPickUp}
          showNumber={showNumbers}
          number={`${number}`}
          isRoundTrip={isRoundTrip}
        />
        <Select
          id={`${baseId}-${number}`}
          ref={primaryWithOverlay ? null : selectElemRef}
          disabled={disabled}
          showSearch
          placeholder={placeholder}
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          value={poi ? `${name} • ${combinedAddress}` : presetKeyword}
          onSearch={primaryWithOverlay ? () => {} : onAddressSearch}
          onSelect={primaryWithOverlay ? () => {} : onSelectPOI}
          onFocus={primaryWithOverlay ? openOverlay : () => {}}
          className={cx(styles.addressField)}
          dropdownClassName={
            primaryWithOverlay || !options || options.length === 0
              ? styles.emptyDropdown
              : cx(styles.addressDropdown, {
                  [styles.addressDropdownMoreItems]:
                    primary && options.length > 2,
                  // differentiate ios css since keyboard isn't taking viewport on ios
                  [styles.overlayDropdown]: overlay && options.length > 2,
                  [styles.overlayDropdownIOS]:
                    overlay && isIOS && options.length > 2,
                })
          }
          optionLabelProp="label"
          size="large"
        >
          {primaryWithOverlay ? null : fetching ? (
            <Select.Option key={'loading'} disabled>
              <Icon type="loading" />
            </Select.Option>
          ) : isSearchPOI ? (
            renderedAddressOptions
          ) : (
            renderedSuggestOptions
          )}
        </Select>
        {primary &&
          allowDelete && (
            <button className={styles.addressClearButton} onClick={onDelete}>
              <img alt="delete-address" src={DeleteIcon} />
            </button>
          )}
      </div>
    );
  };

  return (
    <Fragment>
      {renderAddressSearchBar()}
      {searchInOverlay && (
        <Drawer
          destroyOnClose
          width={widthOverlay}
          height="100%"
          placement={placementOverlay}
          visible={visibleOverlay}
          className={styles.overlayDrawer}
          onClose={closeOverlay}
        >
          <a className={styles.closeButton} onClick={closeOverlay}>
            <img src={closeIcon} alt="close" />
          </a>
          {renderAddressSearchBar(true)}
        </Drawer>
      )}
    </Fragment>
  );
};

AddressField.propTypes = {
  fetchAddressFunc: PropTypes.func,
  isPickUp: PropTypes.bool,
  number: PropTypes.number,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  presetKeyword: PropTypes.string,
  poi: POIPropType,
  showNumbers: PropTypes.bool,
  allowDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  isRoundTrip: PropTypes.bool,
  isPlaceholder: PropTypes.bool,
  searchInOverlay: PropTypes.bool,
  suggestPOIs: PropTypes.shape({}),
  postSavingPoiCallback: PropTypes.func,
};

AddressField.defaultProps = {
  fetchAddressFunc: () => {},
  onChange: () => {},
  number: 1,
  placeholder: '',
  isPickUp: false,
  poi: undefined,
  showNumbers: false,
  allowDelete: false,
  onDelete: undefined,
  autoFocus: false,
  searchInOverlay: false,
  suggestPOIs: undefined,
  presetKeyword: undefined,
  disabled: false,
  isRoundTrip: false,
  isPlaceholder: false,
  dispatch: () => {},
  postSavingPoiCallback: undefined,
};

export default connect((state, props) => ({
  suggestPOIs: suggestPOIsSelector(state, props),
}))(AddressField);
